import { isAxiosError } from 'axios';

import api from '@/shared/api';
import {
  extractApiFieldErrors,
  handleApiError,
  requestExcelBlobExport,
  requestPdfBlobExport,
  withQuery,
} from '@/shared/lib';

import type {
  CreateCombinedDocumentSendResponse,
  CreateDocumentSendPayload,
  DocumentSendApiResponse,
  DocumentSendDetailApiResponse,
  DocumentSendExportParams,
  DocumentSendFilters,
  DocumentSendListParams,
  DocumentSendRecipientReportApiResponse,
  DocumentSendRecipientReportParams,
  DocumentSendReportApiResponse,
} from '../model/types';

const DOCUMENT_SEND_EXPORT_GENERIC_MESSAGE = 'Erro ao exportar. Tente novamente.';
export const DOCUMENT_SEND_PDF_GENERIC_MESSAGE = 'Não foi possível gerar o PDF.';
const userFacingErrors = new WeakSet<Error>();
const fieldErrorsByError = new WeakMap<Error, Record<string, string[]>>();

export const markDocumentSendUserFacingError = <T extends Error>(error: T): T => {
  userFacingErrors.add(error);
  return error;
};

export const isDocumentSendUserFacingError = (error: unknown): error is Error =>
  error instanceof Error && userFacingErrors.has(error);

export const markDocumentSendFieldErrors = <T extends Error>(
  error: T,
  fieldErrors: Record<string, string[]>
): T => {
  if (Object.keys(fieldErrors).length > 0) {
    fieldErrorsByError.set(error, fieldErrors);
  }

  return error;
};

export const getDocumentSendFieldErrors = (error: unknown): Record<string, string[]> =>
  (error instanceof Error ? fieldErrorsByError.get(error) : undefined) ?? {};

const toUserFacingApiError = (error: unknown, defaultMessage: string): Error => {
  const handledError = markDocumentSendFieldErrors(
    handleApiError(error, defaultMessage),
    extractApiFieldErrors(error)
  );

  if (isAxiosError(error) && error.response?.data?.message) {
    return markDocumentSendUserFacingError(handledError);
  }

  return handledError;
};

const toProtheusPartyQuery = (filters: DocumentSendFilters) => ({
  protheusClientCode: filters.protheusClient?.code,
  protheusClientStore: filters.protheusClient?.store,
  protheusSupplierCode: filters.protheusSupplier?.code,
  protheusSupplierStore: filters.protheusSupplier?.store,
});

class DocumentSendApi {
  async getDocumentSends(params: DocumentSendListParams): Promise<DocumentSendApiResponse> {
    try {
      const response = await api.get<DocumentSendApiResponse>(
        withQuery('/document-sends', {
          page: params.page,
          itemsPerPage: params.itemsPerPage,
          term: params.term,
          documentTypes: params.documentTypes,
          formats: params.formats,
          hasElectronicCopy: params.hasElectronicCopy,
          ...toProtheusPartyQuery(params),
          sendDate: params.sendDate,
          sort: params.sort,
          order: params.order,
        })
      );

      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar envios');
    }
  }

  async exportDocumentSendsExcel(
    params: DocumentSendExportParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const query = withQuery('/document-sends/export/excel', {
      term: params.term,
      documentTypes: params.documentTypes,
      formats: params.formats,
      hasElectronicCopy: params.hasElectronicCopy,
      ...toProtheusPartyQuery(params),
      sendDate: params.sendDate,
      sort: params.sort,
      order: params.order,
    });

    return requestExcelBlobExport(
      () => api.get(query, { responseType: 'blob' }),
      'controle-envios.xlsx',
      DOCUMENT_SEND_EXPORT_GENERIC_MESSAGE
    );
  }

  async getDocumentSendDetail(id: number): Promise<DocumentSendDetailApiResponse> {
    try {
      const response = await api.get<DocumentSendDetailApiResponse>(`/document-sends/${id}`);
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar documento de controle de envio');
    }
  }

  async getDetailPdf(id: number): Promise<Blob> {
    return requestPdfBlobExport(
      () =>
        api.get(`/document-sends/${id}/pdf`, {
          responseType: 'blob',
        }),
      DOCUMENT_SEND_PDF_GENERIC_MESSAGE
    );
  }

  async getDocumentSendReportByDocumentRevision(
    documentRevisionId: number
  ): Promise<DocumentSendReportApiResponse> {
    try {
      const response = await api.get<DocumentSendReportApiResponse>(
        '/document-sends/report/by-document',
        {
          params: {
            documentRevisionId,
          },
        }
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar relatório de envios do documento');
    }
  }

  async getReportByDocumentPdf(documentRevisionId: number): Promise<Blob> {
    return requestPdfBlobExport(
      () =>
        api.get('/document-sends/report/by-document/pdf', {
          params: {
            documentRevisionId,
          },
          responseType: 'blob',
        }),
      DOCUMENT_SEND_PDF_GENERIC_MESSAGE
    );
  }

  async getDocumentSendReportByRecipient(
    params: DocumentSendRecipientReportParams
  ): Promise<DocumentSendRecipientReportApiResponse> {
    try {
      const response = await api.get<DocumentSendRecipientReportApiResponse>(
        '/document-sends/report/by-recipient',
        {
          params: {
            recipientType: params.recipientType,
            protheusCode: params.protheusCode,
            protheusStore: params.protheusStore,
          },
        }
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar relatório de envios do destinatário');
    }
  }

  async getReportByRecipientPdf(params: DocumentSendRecipientReportParams): Promise<Blob> {
    return requestPdfBlobExport(
      () =>
        api.get('/document-sends/report/by-recipient/pdf', {
          params: {
            recipientType: params.recipientType,
            protheusCode: params.protheusCode,
            protheusStore: params.protheusStore,
          },
          responseType: 'blob',
        }),
      DOCUMENT_SEND_PDF_GENERIC_MESSAGE
    );
  }

  async deleteDocumentSend(id: number): Promise<void> {
    try {
      await api.delete(`/document-sends/${id}`);
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao excluir envio');
    }
  }

  async createDocumentSend(payload: CreateDocumentSendPayload): Promise<{ id: number }> {
    try {
      const response = await api.post<{ data: { id: number } }>('/document-sends', payload);
      return response.data.data;
    } catch (error: unknown) {
      throw toUserFacingApiError(error, 'Erro ao registrar envio');
    }
  }

  async createElectronicDocumentSend(formData: FormData): Promise<{ id: number }> {
    try {
      const response = await api.post<{ success: boolean; data: { id: number } }>(
        '/document-sends/electronic',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' } }
      );
      return response.data.data;
    } catch (error: unknown) {
      throw toUserFacingApiError(error, 'Erro ao realizar envio eletrônico');
    }
  }

  async createCombinedDocumentSend(
    formData: FormData
  ): Promise<CreateCombinedDocumentSendResponse> {
    try {
      const response = await api.post<{
        success: boolean;
        data: CreateCombinedDocumentSendResponse;
      }>('/document-sends/combined', formData, {
        headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
      });
      return response.data.data;
    } catch (error: unknown) {
      throw toUserFacingApiError(error, 'Erro ao realizar envio eletrônico e físico');
    }
  }
}

export const documentSendApi = new DocumentSendApi();
